import React, { useState } from 'react';
import { Box, Typography, Collapse, Chip, Stack } from '@mui/material';
import { MapPin, User, Phone, Mail, Home, Key } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { AddressDetailsForm } from './forms/AddressDetailsForm';
import { ContactInfoForm } from './forms/ContactInfoForm';
import { DeliveryTimeForm } from './forms/DeliveryTimeForm';
import { SectionHeader, formatTimeRange } from './SectionHeader';

export const DeliverySection = ({
  address,
  targetTime,
  setTargetTime,
  earliestTargetTime,
  setEarliestTargetTime,
  hasTimeWindow,
  setHasTimeWindow,
  pickupTime,
  apartmentNumber, setApartmentNumber,
  floor, setFloor,
  buildingName, setBuildingName,
  accessCode, setAccessCode,
  instructions, setInstructions,
  name, setName,
  phone, setPhone,
  email, setEmail,
  inModal = false,
}) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);

  const hasAddressDetails = apartmentNumber || floor || buildingName || accessCode || instructions;
  const hasContactDetails = name || phone || email;
  const hasAnyDetails = hasAddressDetails || hasContactDetails;

  const summaryItems = [];
  if (name)            summaryItems.push({ icon: <User size={12} />,  label: name });
  if (phone)           summaryItems.push({ icon: <Phone size={12} />, label: phone });
  if (email)           summaryItems.push({ icon: <Mail size={12} />,  label: email });
  if (apartmentNumber) summaryItems.push({ icon: <Home size={12} />,  label: `${t('delivery.form.apartmentNumber', 'Apt')} ${apartmentNumber}` });
  if (floor)           summaryItems.push({ icon: <Home size={12} />,  label: `${t('delivery.form.floor', 'Floor')} ${floor}` });
  if (buildingName)    summaryItems.push({ icon: <Home size={12} />,  label: buildingName });
  if (accessCode)      summaryItems.push({ icon: <Key size={12} />,   label: `${t('delivery.form.accessCode', 'Code')}: ${accessCode}` });
  if (instructions)    summaryItems.push({ icon: null, label: `"${instructions.length > 30 ? instructions.slice(0, 30) + '…' : instructions}"` });

  return (
    <Box>
      <SectionHeader
        icon={<MapPin size={16} />}
        title={t('delivery.deliveryDetails') || 'Delivery Details'}
        subtitle={address}
        timeDisplay={formatTimeRange(hasTimeWindow ? earliestTargetTime : targetTime, targetTime && hasTimeWindow ? targetTime : null)}
        expanded={expanded}
        onToggle={() => setExpanded(!expanded)}
        inModal={inModal}
      />

      {!expanded && hasAnyDetails && (
        <Box sx={{ pl: inModal ? 3.5 : 4.5, pt: 0.5, pb: 1 }}>
          <Stack direction="row" flexWrap="wrap" gap={0.75}>
            {summaryItems.map((item, idx) => (
              <Chip
                key={idx}
                icon={item.icon || undefined}
                label={item.label}
                size="small" variant="outlined"
                sx={{ height: 24, fontSize: '0.75rem', '& .MuiChip-icon': { marginLeft: '6px' }, '& .MuiChip-label': { px: 1 } }}
              />
            ))}
          </Stack>
        </Box>
      )}

      <Collapse in={expanded}>
        <Box sx={{ pl: inModal ? 3.5 : 4.5, pt: 1.5 }}>
          <Typography variant="caption" fontWeight={600} color="text.secondary"
            sx={{ textTransform: 'uppercase', letterSpacing: 0.5, mb: 1.5, display: 'block' }}>
            {t('delivery.targetTime') || 'Target Delivery Time'}
          </Typography>
          <Box sx={{ mb: 3 }}>
            <DeliveryTimeForm
              targetTime={targetTime}
              setTargetTime={setTargetTime}
              earliestTargetTime={earliestTargetTime}
              setEarliestTargetTime={setEarliestTargetTime}
              hasTimeWindow={hasTimeWindow}
              setHasTimeWindow={setHasTimeWindow}
              pickupTime={pickupTime}
            />
          </Box>

          <Typography variant="caption" fontWeight={600} color="text.secondary"
            sx={{ textTransform: 'uppercase', letterSpacing: 0.5, mb: 1.5, display: 'block' }}>
            {t('delivery.addressDetails') || 'Address Details'}
          </Typography>
          <Box sx={{ mb: 3 }}>
            <AddressDetailsForm
              apartmentNumber={apartmentNumber} setApartmentNumber={setApartmentNumber}
              floor={floor} setFloor={setFloor}
              buildingName={buildingName} setBuildingName={setBuildingName}
              accessCode={accessCode} setAccessCode={setAccessCode}
              instructions={instructions} setInstructions={setInstructions}
              isPickup={false}
            />
          </Box>

          <Typography variant="caption" fontWeight={600} color="text.secondary"
            sx={{ textTransform: 'uppercase', letterSpacing: 0.5, mb: 1.5, display: 'block' }}>
            {t('delivery.recipientInformation') || 'Recipient Information'}
          </Typography>
          <Box>
            <ContactInfoForm
              name={name} setName={setName}
              phone={phone} setPhone={setPhone}
              email={email} setEmail={setEmail}
              isSender={false}
            />
          </Box>
        </Box>
      </Collapse>
    </Box>
  );
};